import {chain} from 'lodash';

import {isTeamInbox} from '../../../../../src/helpers/modelHelpers';
import {
  BasicActivityArchiveDecisionTreeModel,
  BasicActivityAssigneeModel,
  BasicActivityConversationStageUpdateModel,
  BasicActivityCustomAttributeModel,
  BasicActivityErrorModel,
  BasicActivityInboxModel,
  BasicActivityModel,
  BasicActivityRuleForwardModel,
  BasicActivityTagModel,
  BasicActivityTopicModel,
  BasicActivityTrackerArchiveModel,
  BasicActivityTrackerShareModel,
  BasicActivityTrackerUnshareModel,
  BasicActivityTypesEnum,
  BasicVisualTypesEnum,
} from '../../../../../src/models/api/activityModel';
import {
  ActivitySourceModel,
  ActivitySourceTypesEnum,
  ActivitySourceVisualTypesEnum,
} from '../../../../../src/models/api/activitySourceModel';
import {AppState} from '../../../../../src/store/appState';
import {getCustomFieldsForConversations} from '../../../../../src/store/data/customFields/customFieldsSelectors';
import {getInboxes} from '../../../../../src/store/data/inboxes/inboxesSelectors';
import {getTags} from '../../../../../src/store/data/tags/tagsSelectors';
import {getFindTeammate, getUserId} from '../../../../../src/store/data/teammates/teammatesSelectors';
import {getTopics} from '../../../../../src/store/data/topics/topicsSelectors';
import {createSelector} from '../../../../helpers/reselectHelpers';
import {
  ConversationStageUpdateByCompanyVisualBasicActivity,
  ConversationStageUpdateByRuleVisualBasicActivity,
  VisualBasicActivity,
  VisualBasicActivityTypesEnum,
} from './visualBasicActivity';

/*
 * Props.
 */

export interface VisualBasicActivityProps {
  activity: BasicActivityModel;
}

/*
 * Selectors.
 */

function getActivity(_state: AppState, props: VisualBasicActivityProps) {
  return props.activity;
}

function makeGetSourceTeammate(activitySource: ActivitySourceModel) {
  return createSelector([getFindTeammate], (findTeammate) => {
    if (activitySource.visualType !== ActivitySourceVisualTypesEnum.TEAMMATE) {
      return undefined;
    }

    return findTeammate(activitySource.teammateId);
  });
}

function makeGetAssignee(activity: BasicActivityAssigneeModel) {
  return createSelector([getFindTeammate], (findTeammate) => findTeammate(activity.assigneeId));
}

// New inbox for activities such as 'move'. This won't be the original inbox id.
function makeGetInbox(activity: BasicActivityInboxModel) {
  return createSelector([getInboxes], (inboxes) => inboxes[activity.inboxId]);
}

// The team inboxes for this activity prior to moving.
function makeGetOriginalTeamInboxes(activity: BasicActivityModel) {
  return createSelector([getInboxes], (inboxes) => {
    if (!activity.inboxIds) {
      return [];
    }

    return chain(activity.inboxIds)
      .map((inboxId) => inboxes[inboxId])
      .compact()
      .filter((inbox) => isTeamInbox(inbox))
      .value();
  });
}

function makeGetTag(activity: BasicActivityTagModel) {
  return createSelector([getTags], (tags) => tags[activity.tagId]);
}

function makeGetCustomField(activity: BasicActivityCustomAttributeModel) {
  return createSelector(
    [getCustomFieldsForConversations],
    (customFields) => customFields[activity.customFieldId],
  );
}

function makeGetRecipient(
  activity:
    | BasicActivityTrackerShareModel
    | BasicActivityTrackerUnshareModel
    | BasicActivityTrackerArchiveModel,
) {
  return createSelector([getFindTeammate], (findTeammate) => {
    if (!activity.recipientId) {
      return undefined;
    }

    return findTeammate(activity.recipientId);
  });
}

export function makeGetVisualBasicActivity() {
  const getVisualBasicActivitySelector = createSelector([getActivity], (activity) => {
    switch (activity.basicVisualType) {
      case BasicVisualTypesEnum.ASSIGNEE:
        return makeGetVisualBasicActivityWithAssignee(activity);

      case BasicVisualTypesEnum.INBOX:
        return makeGetVisualBasicActivityWithInbox(activity);

      case BasicVisualTypesEnum.TAG:
        return makeGetVisualBasicActivityWithTag(activity);

      case BasicVisualTypesEnum.ARCHIVED_DECISION_TREE:
        return makeGetVisualBasicActivityWithArchiveDecisionTree(activity);

      case BasicVisualTypesEnum.TOPIC:
        return makeGetVisualBasicActivityWithTopic(activity);

      case BasicVisualTypesEnum.CUSTOM_ATTRIBUTE:
        return makeGetVisualBasicActivityWithCustomField(activity);

      case BasicVisualTypesEnum.ERROR:
        return makeGetVisualBasicActivityWithError(activity);

      case BasicVisualTypesEnum.RULE_FORWARD:
        return makeGetVisualBasicActivityWithContactHandle(activity);

      case BasicVisualTypesEnum.TRACKER_ARCHIVE:
        return makeGetVisualBasicActivityWithTrackerArchive(activity);

      case BasicVisualTypesEnum.TRACKER_SHARE:
        return makeGetVisualBasicActivityWithTrackerShare(activity);

      case BasicVisualTypesEnum.TRACKER_UNSHARE:
        return makeGetVisualBasicActivityWithTrackerUnshare(activity);

      case BasicVisualTypesEnum.CONVERSATION_STAGE_UPDATE:
        return makeGetVisualBasicActivityWithStage(activity);

      default:
        return makeGetVisualBasicActivityWithoutDependency(activity);
    }
  });

  return (state: AppState, props: VisualBasicActivityProps): VisualBasicActivity | undefined => {
    const selector = getVisualBasicActivitySelector(state, props);
    return selector(state);
  };
}

/**
 * Build an activity for an assignment,
 * while checking whether the source was from a teammate or rule.
 */
function makeGetVisualBasicActivityWithAssignee(activity: BasicActivityAssigneeModel) {
  const getSourceTeammate = makeGetSourceTeammate(activity.source);
  const getAssignee = makeGetAssignee(activity);

  return createSelector(
    [getUserId, getSourceTeammate, getAssignee],
    (userId, sourceTeammate, assignee): VisualBasicActivity | undefined => {
      if (!assignee) {
        return undefined;
      }

      const {source} = activity;

      // If the current user assigned this conversation to themselves.
      if (
        source.type === ActivitySourceTypesEnum.TEAMMATE &&
        sourceTeammate &&
        sourceTeammate.id === userId &&
        sourceTeammate.id === assignee.id
      ) {
        return {type: VisualBasicActivityTypesEnum.ASSIGNED_USER_THEMSELVES};
      }

      // If one teammate assigned this to themselves.
      if (
        source.type === ActivitySourceTypesEnum.TEAMMATE &&
        sourceTeammate &&
        sourceTeammate.id === assignee.id
      ) {
        return {type: VisualBasicActivityTypesEnum.ASSIGNED_TEAMMATE_THEMSELVES, assignee};
      }

      // If one teammate assigned this to another.
      if (source.type === ActivitySourceTypesEnum.TEAMMATE && sourceTeammate) {
        return {type: VisualBasicActivityTypesEnum.ASSIGNED_BY_TEAMMATE, assignee, sourceTeammate};
      }

      // If teammate was assigned to by a rule.
      if (
        source.type === ActivitySourceTypesEnum.RULE &&
        source.visualType === ActivitySourceVisualTypesEnum.RULE
      ) {
        return {type: VisualBasicActivityTypesEnum.ASSIGNED_BY_RULE, assignee, source};
      }

      // If teammate was assigned to by an API.
      if (
        source.type === ActivitySourceTypesEnum.PUBLIC_API &&
        source.visualType === ActivitySourceVisualTypesEnum.OTHER
      ) {
        return {type: VisualBasicActivityTypesEnum.ASSIGNED_BY_API, assignee, source};
      }

      // Return the old format for uncaptured cases.
      return {type: VisualBasicActivityTypesEnum.ASSIGNED_OTHER, assignee};
    },
  );
}

/** Build an activity which indicates that the conversation was moved to a new inbox. */
function makeGetVisualBasicActivityWithInbox(activity: BasicActivityInboxModel) {
  const getSourceTeammate = makeGetSourceTeammate(activity.source);
  const getInbox = makeGetInbox(activity);

  return createSelector(
    [getSourceTeammate, getInbox],
    (sourceTeammate, inbox): VisualBasicActivity | undefined => {
      if (!inbox) {
        return undefined;
      }

      const {source} = activity;

      // Conversation was moved by a teammate.
      if (
        source.type === ActivitySourceTypesEnum.TEAMMATE &&
        source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE &&
        sourceTeammate
      ) {
        return {type: VisualBasicActivityTypesEnum.MOVED_BY_TEAMMATE, inbox, sourceTeammate};
      }

      // Conversation was moved by a rule.
      if (
        source.type === ActivitySourceTypesEnum.RULE &&
        source.visualType === ActivitySourceVisualTypesEnum.RULE
      ) {
        return {type: VisualBasicActivityTypesEnum.MOVED_BY_RULE, inbox, source};
      }

      // Return the old format for uncaptured cases.
      return {type: VisualBasicActivityTypesEnum.MOVED_BY_OTHER, inbox};
    },
  );
}

/**
 * Build an activity for both tagging and untagging.
 * Find the source and return the corresponding payload.
 */
function makeGetVisualBasicActivityWithTag(activity: BasicActivityTagModel) {
  const getSourceTeammate = makeGetSourceTeammate(activity.source);
  const getTag = makeGetTag(activity);

  return createSelector(
    [getSourceTeammate, getTag],
    (sourceTeammate, tag): VisualBasicActivity | undefined => {
      if (!tag) {
        return undefined;
      }

      // If tag was not from a rule and we cannot determine the tag source, return the old format as a fallback.
      switch (activity.type) {
        case BasicActivityTypesEnum.TAG:
          return {
            type: VisualBasicActivityTypesEnum.TAG,
            tag,
            sourceTeammate,
            decisionTreeId: activity.decisionTreeId,
            decisionTreeName: activity.decisionTreeName,
            decisionTreeNamespace: activity.decisionTreeNamespace,
          };

        case BasicActivityTypesEnum.UNTAG:
          return {type: VisualBasicActivityTypesEnum.UNTAG, tag, sourceTeammate};

        default:
          return undefined;
      }
    },
  );
}

/**
 * Build an activity for Archive activities created by a decision tree.
 */
function makeGetVisualBasicActivityWithArchiveDecisionTree(activity: BasicActivityArchiveDecisionTreeModel) {
  const getOriginalTeamInboxes = makeGetOriginalTeamInboxes(activity);

  return createSelector([getOriginalTeamInboxes], (originalTeamInboxes): VisualBasicActivity | undefined => ({
    type: VisualBasicActivityTypesEnum.ARCHIVED_DECISION_TREE,
    originalTeamInboxes,
    decisionTreeId: activity.decisionTreeId,
    decisionTreeName: activity.decisionTreeName,
    decisionTreeNamespace: activity.decisionTreeNamespace,
  }));
}

/** Build an activity for conversation's stage update. */
function makeGetVisualBasicActivityWithStage(activity: BasicActivityConversationStageUpdateModel) {
  if (
    activity.source.type === ActivitySourceTypesEnum.RULE &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.RULE
  ) {
    const result: ConversationStageUpdateByRuleVisualBasicActivity = {
      type: VisualBasicActivityTypesEnum.CONVERSATION_STAGE_UPDATE_BY_RULE,
      conversationStageType: activity.conversationStageType,
      source: activity.source,
    };

    return () => result;
  }

  const result: ConversationStageUpdateByCompanyVisualBasicActivity = {
    type: VisualBasicActivityTypesEnum.CONVERSATION_STAGE_UPDATE_BY_COMPANY,
    conversationStageType: activity.conversationStageType,
    source: activity.source,
  };

  return () => result;
}

/** Build activity for various 3rd party integrations such as JIRA and GitHub. */
function makeGetVisualBasicActivityWithTopic(activity: BasicActivityTopicModel) {
  const getSourceTeammate = makeGetSourceTeammate(activity.source);

  return createSelector(
    [getSourceTeammate, getTopics],
    (sourceTeammate, topics): VisualBasicActivity | undefined => {
      const {topicId} = activity;

      if (!topicId) {
        return undefined;
      }

      const topic = topics[topicId];
      if (!topic) {
        return undefined;
      }

      if (activity.type === BasicActivityTypesEnum.TOPIC_CLOSED) {
        return {
          type: VisualBasicActivityTypesEnum.TOPIC_CLOSED,
          topic,
          sourceTeammate,
        };
      }

      if (activity.type === BasicActivityTypesEnum.TOPIC_ADDED) {
        return {
          type: VisualBasicActivityTypesEnum.TOPIC_ADDED,
          topic,
          sourceTeammate,
        };
      }

      if (activity.type === BasicActivityTypesEnum.TOPIC_REMOVED) {
        return {
          type: VisualBasicActivityTypesEnum.TOPIC_REMOVED,
          topic,
          sourceTeammate,
        };
      }

      return undefined;
    },
  );
}

function makeGetVisualBasicActivityWithCustomField(activity: BasicActivityCustomAttributeModel) {
  const getSourceTeammate = makeGetSourceTeammate(activity.source);
  const getCustomField = makeGetCustomField(activity);

  return createSelector(
    [getSourceTeammate, getCustomField],
    (sourceTeammate, customField): VisualBasicActivity | undefined => {
      if (!customField) {
        return undefined;
      }

      return {type: VisualBasicActivityTypesEnum.CUSTOM_ATTRIBUTE_UPDATED, customField, sourceTeammate};
    },
  );
}

/** Build an activity which accounts for general errors that can be displayed in the timeline. */
function makeGetVisualBasicActivityWithError(activity: BasicActivityErrorModel) {
  if (activity.source.visualType !== ActivitySourceVisualTypesEnum.CONTACT) {
    return () => undefined;
  }

  const result: VisualBasicActivity = {
    type: VisualBasicActivityTypesEnum.ERROR,
    activityError: activity.error,
    activitySource: activity.source,
  };
  return () => result;
}

/** Build an activity for a rule forward action. */
function makeGetVisualBasicActivityWithContactHandle({source, contactHandle}: BasicActivityRuleForwardModel) {
  // If teammate was forwarded to by a rule.
  if (
    source.type === ActivitySourceTypesEnum.RULE &&
    source.visualType === ActivitySourceVisualTypesEnum.RULE
  ) {
    const result: VisualBasicActivity = {
      type: VisualBasicActivityTypesEnum.FORWARDED_BY_RULE,
      contactHandle,
      source,
    };
    return () => result;
  }

  // Return old format if no conditions matched.
  const result: VisualBasicActivity = {
    type: VisualBasicActivityTypesEnum.FORWARDED_BY_OTHER,
    contactHandle,
  };
  return () => result;
}

/** Build an activity for users who are removed from a conversation. */
function makeGetVisualBasicActivityWithTrackerUnshare(activity: BasicActivityTrackerUnshareModel) {
  const getSourceTeammate = makeGetSourceTeammate(activity.source);
  const getRecipient = makeGetRecipient(activity);

  return createSelector(
    [getUserId, getSourceTeammate, getRecipient],
    (userId, sourceTeammate, recipient): VisualBasicActivity | undefined => {
      if (!recipient || !sourceTeammate) {
        return undefined;
      }

      if (recipient.id === userId) {
        return {
          type: VisualBasicActivityTypesEnum.TRACKER_UNSHARE_USER_BY_TEAMMATE,
          sourceTeammate,
        };
      }

      return {type: VisualBasicActivityTypesEnum.TRACKER_UNSHARE_BY_TEAMMATE, recipient, sourceTeammate};
    },
  );
}

function makeGetVisualBasicActivityWithTrackerArchive(activity: BasicActivityTrackerArchiveModel) {
  const getRecipient = makeGetRecipient(activity);

  return createSelector([getUserId, getRecipient], (userId, recipient): VisualBasicActivity | undefined => {
    if (!recipient) {
      return undefined;
    }

    const {source} = activity;

    // Only render teammate's own tracker_archive activities when triggered by a rule and viewed from your individual inbox.
    if (
      source.type === ActivitySourceTypesEnum.RULE &&
      source.visualType === ActivitySourceVisualTypesEnum.RULE &&
      recipient.id === userId
    ) {
      return {type: VisualBasicActivityTypesEnum.TRACKER_ARCHIVE_USER_BY_RULE, source};
    }

    // Only render when action was performed by rule.
    return undefined;
  });
}

/** Build an activity for teammates that are added or notified to the conversation. */
function makeGetVisualBasicActivityWithTrackerShare(activity: BasicActivityTrackerShareModel) {
  const getSourceTeammate = makeGetSourceTeammate(activity.source);
  const getRecipient = makeGetRecipient(activity);

  return createSelector(
    [getUserId, getSourceTeammate, getRecipient],
    (userId, sourceTeammate, recipient): VisualBasicActivity | undefined => {
      if (!recipient) {
        return undefined;
      }

      const {source} = activity;

      // If the source type is teammate, a teammate was manually invited.
      if (
        source.type === ActivitySourceTypesEnum.TEAMMATE &&
        source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE &&
        sourceTeammate
      ) {
        if (recipient.id === userId) {
          // If current user was invited to the conversation.
          return {type: VisualBasicActivityTypesEnum.TRACKER_SHARE_USER_BY_TEAMMATE, sourceTeammate};
        }

        return {type: VisualBasicActivityTypesEnum.TRACKER_SHARE_BY_TEAMMATE, recipient, sourceTeammate};
      }

      // If the source type is rule, a teammate was notified via rule.
      if (
        source.type === ActivitySourceTypesEnum.RULE &&
        source.visualType === ActivitySourceVisualTypesEnum.RULE
      ) {
        return {type: VisualBasicActivityTypesEnum.TRACKER_SHARE_BY_RULE, recipient, source};
      }

      // If the source type is a rule which has been deleted.
      if (
        source.type === ActivitySourceTypesEnum.RULE &&
        source.visualType === ActivitySourceVisualTypesEnum.OTHER &&
        source.ruleWasDeleted
      ) {
        return {type: VisualBasicActivityTypesEnum.TRACKER_SHARE_BY_DELETED_RULE, recipient};
      }

      // If none of the above conditions are matched, return the old format for this activity.
      return {type: VisualBasicActivityTypesEnum.TRACKER_SHARE_OTHER, recipient};
    },
  );
}

/** Build an activity for cases where the basicVisualType is not enough.  */
function makeGetVisualBasicActivityWithoutDependency(activity: BasicActivityModel) {
  const getSourceTeammate = makeGetSourceTeammate(activity.source);
  const getOriginalTeamInboxes = makeGetOriginalTeamInboxes(activity);

  return createSelector(
    [getUserId, getSourceTeammate, getOriginalTeamInboxes],
    (userId, sourceTeammate, originalTeamInboxes): VisualBasicActivity | undefined => {
      const {source} = activity;

      switch (activity.type) {
        // Handle activities corresponding to the conversation being unassigned from a teammate.
        case BasicActivityTypesEnum.UNASSIGN: {
          // Check if the conversation was unassigned due there being no activity.
          if (
            source.type === ActivitySourceTypesEnum.INACTIVE &&
            source.visualType === ActivitySourceVisualTypesEnum.OTHER
          ) {
            return {type: VisualBasicActivityTypesEnum.UNASSIGNED_INACTIVITY};
          }

          // Check if the conversation was unassigned directly by a teammate (or you).
          if (
            source.type === ActivitySourceTypesEnum.TEAMMATE &&
            source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE &&
            !source.wasUnavailable &&
            sourceTeammate
          ) {
            // Conversation was unassigned by the current user.
            if (sourceTeammate.id === userId) {
              return {type: VisualBasicActivityTypesEnum.UNASSIGNED_USER};
            }

            // Conversation was unassigned by another teammate.
            return {type: VisualBasicActivityTypesEnum.UNASSIGNED_TEAMMATE, sourceTeammate};
          }

          // Check if the conversation was unassigned because that teammate was out of office.
          if (
            source.type === ActivitySourceTypesEnum.TEAMMATE &&
            source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE &&
            sourceTeammate &&
            source.wasUnavailable
          ) {
            return {type: VisualBasicActivityTypesEnum.UNASSIGNED_OOO, sourceTeammate};
          }

          // Check if the conversation was unassigned because the teammate was deleted from the company.
          if (
            source.type === ActivitySourceTypesEnum.COMPANY &&
            source.visualType === ActivitySourceVisualTypesEnum.OTHER
          ) {
            return {type: VisualBasicActivityTypesEnum.UNASSIGNED_DELETED_TEAMMATE};
          }

          // If none of the above assignment conditions are matched, return the old format.
          return {type: VisualBasicActivityTypesEnum.UNASSIGNED_OTHER};
        }

        // Handle activities for archival of conversations.
        // NOTE: Snooze activities fall under the archive type as well.
        case BasicActivityTypesEnum.ARCHIVE: {
          if (originalTeamInboxes.length) {
            if (source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE && sourceTeammate) {
              // Conversation was archived from a snooze belonging to a teammate.
              if (activity.basicVisualType === BasicVisualTypesEnum.REMINDER) {
                return {
                  type: VisualBasicActivityTypesEnum.ARCHIVED_SNOOZE_TEAMMATE,
                  originalTeamInboxes,
                  sourceTeammate,
                };
              }

              // Conversation was directly archived by a teammate.
              return {
                type: VisualBasicActivityTypesEnum.ARCHIVED_TEAMMATE,
                originalTeamInboxes,
                sourceTeammate,
              };
            }

            if (
              source.type === ActivitySourceTypesEnum.RULE &&
              source.visualType === ActivitySourceVisualTypesEnum.RULE
            ) {
              // Conversation was snoozed because of a rule.
              if (activity.basicVisualType === BasicVisualTypesEnum.REMINDER) {
                return {
                  type: VisualBasicActivityTypesEnum.ARCHIVED_SNOOZE_RULE,
                  originalTeamInboxes,
                  source,
                };
              }

              // Conversation was archived because of a rule.
              return {type: VisualBasicActivityTypesEnum.ARCHIVED_RULE, originalTeamInboxes, source};
            }
          }

          if (activity.basicVisualType === BasicVisualTypesEnum.REMINDER) {
            return {type: VisualBasicActivityTypesEnum.ARCHIVED_SNOOZE_OTHER};
          }

          // If teammate was not the source of the archive, return old format.
          return {type: VisualBasicActivityTypesEnum.ARCHIVED_OTHER};
        }

        // Handle activities for un-archival of conversations.
        case BasicActivityTypesEnum.REOPEN: {
          if (source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE && sourceTeammate) {
            // Conversation was reopened by the current user.
            if (sourceTeammate.id === userId) {
              return {type: VisualBasicActivityTypesEnum.REOPEN_USER};
            }

            // Conversation was reopened by another teammate.
            return {type: VisualBasicActivityTypesEnum.REOPEN_TEAMMATE, sourceTeammate};
          }

          // Conversation was reopened by a rule.
          // NOTE: The rule information is not available on this activity.
          if (
            source.type === ActivitySourceTypesEnum.RULE &&
            source.visualType === ActivitySourceVisualTypesEnum.OTHER
          ) {
            return {type: VisualBasicActivityTypesEnum.REOPEN_RULE};
          }

          // Conversation was reopened by a message being sent.
          if (
            source.type === ActivitySourceTypesEnum.SENDING &&
            source.visualType === ActivitySourceVisualTypesEnum.OTHER
          ) {
            return {type: VisualBasicActivityTypesEnum.REOPEN_MESSAGE_SENT};
          }

          // If conversation was not reopened by a teammate, return the old format.
          return {type: VisualBasicActivityTypesEnum.REOPEN_OTHER};
        }

        // Handle activities for deleting conversations.
        case BasicActivityTypesEnum.TRASH: {
          if (
            source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE &&
            sourceTeammate &&
            originalTeamInboxes.length > 0
          ) {
            return {type: VisualBasicActivityTypesEnum.TRASHED_TEAMMATE, originalTeamInboxes, sourceTeammate};
          }

          if (
            source.type === ActivitySourceTypesEnum.SPAM_FILTER &&
            source.visualType === ActivitySourceVisualTypesEnum.OTHER &&
            originalTeamInboxes.length
          ) {
            return {type: VisualBasicActivityTypesEnum.TRASHED_SPAM, originalTeamInboxes};
          }

          if (source.type === ActivitySourceTypesEnum.DATA_RETENTION) {
            return {type: VisualBasicActivityTypesEnum.TRASHED_DATA_RETENTION};
          }

          // If conversation was not deleted by a teammate, return the old format.
          return {type: VisualBasicActivityTypesEnum.TRASHED_OTHER};
        }

        // Handle activities for merging conversations.
        case BasicActivityTypesEnum.MERGE: {
          if (source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE && sourceTeammate) {
            return {type: VisualBasicActivityTypesEnum.MERGE_TEAMMATE, sourceTeammate};
          }

          // If conversation was not merged by a teammate, return the old format.
          return {type: VisualBasicActivityTypesEnum.MERGE_OTHER};
        }

        // Handle activities for restoring a deleted conversation.
        case BasicActivityTypesEnum.RESTORE: {
          if (
            source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE &&
            sourceTeammate &&
            originalTeamInboxes.length > 0
          ) {
            return {type: VisualBasicActivityTypesEnum.RESTORE_TEAMMATE, originalTeamInboxes, sourceTeammate};
          }

          // If conversation was not restored by a teammate, return the old format.
          return {type: VisualBasicActivityTypesEnum.RESTORE_OTHER};
        }

        // Handle activities for the current user being notified of a conversation by a rule.
        case BasicActivityTypesEnum.RULE_NOTIFY: {
          // If data on the rule is still available, return that information.
          if (
            source.type === ActivitySourceTypesEnum.RULE &&
            source.visualType === ActivitySourceVisualTypesEnum.RULE
          ) {
            return {type: VisualBasicActivityTypesEnum.RULE_NOTIFIED_EXISTING_RULE, source};
          }

          // If we no longer have the rule (it was likely deleted), return a generic message.
          if (
            source.type === ActivitySourceTypesEnum.RULE &&
            source.visualType === ActivitySourceVisualTypesEnum.OTHER
          ) {
            return {type: VisualBasicActivityTypesEnum.RULE_NOTIFIED_DELETED_RULE};
          }

          // If type and visual types do not match for this notify activity, return the old format.
          return {type: VisualBasicActivityTypesEnum.RULE_NOTIFIED_OTHER};
        }

        // Handle activities for when a conversation is reopened by a reminder.
        case BasicActivityTypesEnum.REMINDER:
        case BasicActivityTypesEnum.TRACKER_REMINDER:
          return {type: VisualBasicActivityTypesEnum.REMINDER_EXPIRED};

        // Handle activities for when a conversation is split into a new conversation.
        case BasicActivityTypesEnum.SPLIT_ORIGIN: {
          // A message was moved to a new conversation directly by a teammate.
          if (
            activity.basicVisualType === BasicVisualTypesEnum.SPLIT_ORIGIN &&
            source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE &&
            sourceTeammate
          ) {
            return {
              type: VisualBasicActivityTypesEnum.SPLIT_ORIGIN_TEAMMATE,
              targetConversationId: activity.targetConversationId,
              sourceTeammate,
            };
          }

          // A message was moved to a new conversation by a rule.
          if (
            activity.basicVisualType === BasicVisualTypesEnum.SPLIT_ORIGIN &&
            source.visualType === ActivitySourceVisualTypesEnum.RULE
          ) {
            return {
              type: VisualBasicActivityTypesEnum.SPLIT_ORIGIN_RULE,
              targetConversationId: activity.targetConversationId,
              source,
            };
          }

          // A message was moved for privacy reasons.
          if (
            activity.basicVisualType === BasicVisualTypesEnum.SPLIT_ORIGIN &&
            source.type === ActivitySourceTypesEnum.DATA_LEAK
          ) {
            return {
              type: VisualBasicActivityTypesEnum.SPLIT_ORIGIN_DATA_LEAK,
              targetConversationId: activity.targetConversationId,
            };
          }

          if (
            activity.basicVisualType === BasicVisualTypesEnum.SPLIT_ORIGIN &&
            source.type === ActivitySourceTypesEnum.INACTIVE
          ) {
            return {
              type: VisualBasicActivityTypesEnum.AUTO_SPLIT_ORIGIN_INACTIVITY,
              targetConversationId: activity.targetConversationId,
            };
          }

          if (
            activity.basicVisualType === BasicVisualTypesEnum.SPLIT_ORIGIN &&
            source.type === ActivitySourceTypesEnum.LINK_CONVERSATION
          ) {
            return {
              type: VisualBasicActivityTypesEnum.AUTO_SPLIT_ORIGIN_LINK_CONVERSATION,
              targetConversationId: activity.targetConversationId,
            };
          }

          // If the source of this split was not a teammate and was not for privacy reasons, show the old format.
          return {type: VisualBasicActivityTypesEnum.SPLIT_ORIGIN_OTHER};
        }

        // Handle activities for when a conversation has been split from an existing conversation.
        case BasicActivityTypesEnum.SPLIT_TARGET: {
          if (
            activity.basicVisualType === BasicVisualTypesEnum.SPLIT_TARGET &&
            source.visualType === ActivitySourceVisualTypesEnum.RULE
          ) {
            return {
              type: VisualBasicActivityTypesEnum.SPLIT_TARGET_RULE,
              source,
              originConversationId: activity.originConversationId,
            };
          }

          // Case where conversation was split for privacy reasons.
          if (activity.basicVisualType === BasicVisualTypesEnum.SPLIT_TARGET) {
            // We no longer handle the split target case in the timeline, although it is an activity.
            // It is rendered in conversationTimeline.tsx as it must always remain at the top.
            return undefined;
          }

          // If conversation was not split by a teammate or for privacy reasons, return the old format.
          return {type: VisualBasicActivityTypesEnum.SPLIT_TARGET_OTHER};
        }

        case BasicActivityTypesEnum.FRONT_CHAT_TRANSCRIPT_SENT:
          if (activity.basicVisualType === BasicVisualTypesEnum.FRONT_CHAT_TRANSCRIPT_SENT) {
            return {
              type: VisualBasicActivityTypesEnum.FRONT_CHAT_TRANSCRIPT_SENT,
              transcriptRecipientHandle: activity.frontChatTranscriptRecipientHandle,
            };
          }
          return undefined;

        case BasicActivityTypesEnum.FRONT_CHAT_TRANSCRIPT_EMAIL:
          if (activity.basicVisualType === BasicVisualTypesEnum.FRONT_CHAT_TRANSCRIPT_EMAIL) {
            return {
              type: VisualBasicActivityTypesEnum.FRONT_CHAT_TRANSCRIPT_EMAIL,
              transcriptRecipientHandle: activity.frontChatTranscriptRecipientHandle,
            };
          }
          return undefined;

        // Handle activities for unlinking conversations.
        case BasicActivityTypesEnum.CONVERSATION_UNLINKED: {
          if (source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE && sourceTeammate) {
            return {type: VisualBasicActivityTypesEnum.CONVERSATION_UNLINKED, sourceTeammate};
          }

          return undefined;
        }
        case BasicActivityTypesEnum.ACTION_SKIPPED:
          if (
            activity.basicVisualType === BasicVisualTypesEnum.ACTION_SKIPPED &&
            source.visualType === ActivitySourceVisualTypesEnum.RULE
          ) {
            return {
              type: VisualBasicActivityTypesEnum.ACTION_SKIPPED,
              skippedActionType: activity.skippedActionType,
              skipReason: activity.skipReason,
              source,
            };
          }

          return undefined;

        default:
          return undefined;
      }
    },
  );
}
